import { useQuery } from "@tanstack/react-query"

import { getMyFacilityActions } from "@/libs/permissionsApi"

/**
 * useMyFacilityActions — the caller's facility-scoped RBAC action set at a
 * facility (backend #544, GET /permissions/facilities/{id}/my-actions).
 *
 * The role-aware business sidebar + ⌘K palette read this to scope nav to what
 * the operator can actually DO: owner -> every action, non-member -> []. This is
 * a CLIENT HINT for nav VISIBILITY only — the server still enforces every action
 * on the real endpoints, so a hidden route reached by URL still 403s.
 *
 *   - `enabled` only when a facilityId is set — an operator with no facility
 *     selected fires no request.
 *   - FAIL-CLOSED by construction: `actions` is `[]` until the real set resolves
 *     (loading / disabled / error), so gated nav items stay HIDDEN rather than
 *     flashing owner chrome to a non-owner. The consumer decides when the empty
 *     set means "hide gated items" vs "no facility to scope against".
 *   - cached for the session (the action set changes only on a role change),
 *     so the common owner case resolves instantly after the first load.
 */

const FACILITY_ACTIONS_STALE_TIME_MS = 5 * 60_000

export interface MyFacilityActions {
  actions: string[]
  isLoading: boolean
  /** Current successful, idle server read; cached errored/paused reads are not mutation authority. */
  isReady: boolean
  /** An answer (actions or an error) has arrived at least once; stays true while it refetches. */
  isSettled?: boolean
  /** The last read failed (the action set is then empty: fail closed). */
  isError?: boolean
  refetch?: () => void
}

export function useMyFacilityActions(
  facilityId: string | null | undefined,
): MyFacilityActions {
  const query = useQuery({
    queryKey: ["facility-actions", facilityId],
    queryFn: () => getMyFacilityActions(facilityId as string),
    enabled: Boolean(facilityId),
    staleTime: FACILITY_ACTIONS_STALE_TIME_MS,
    refetchOnWindowFocus: false,
  })

  return {
    actions: query.data?.actions ?? [],
    isLoading: query.isLoading,
    isReady: query.status === "success" && query.fetchStatus === "idle",
    isSettled: query.isFetched,
    isError: query.isError,
    refetch: () => {
      void query.refetch()
    },
  }
}
